import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LightBulbIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  WrenchScrewdriverIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  TrashIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import aiService, { ChatLog, AnalysisResponse, Suggestion, QuestionTheme, SavedSuggestion } from '../../services/ai.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

const AIManagement: React.FC = () => {
  // Chat logs state
  const [logs, setLogs] = useState<ChatLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 50;

  // Filters
  const [successFilter, setSuccessFilter] = useState<'true' | 'false' | ''>('');
  const [userFilter, setUserFilter] = useState('');

  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Document upload state
  const [indexStatus, setIndexStatus] = useState<{
    status: string;
    pinecone_configured: boolean;
    openai_configured: boolean;
    anthropic_configured: boolean;
    index_exists: boolean;
    document_count: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [pageName, setPageName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [indexedPages, setIndexedPages] = useState<Array<{ page: string; vector_count: number; sample_sections: string[] }>>([]);
  const [deletingPage, setDeletingPage] = useState<string | null>(null);

  // Saved suggestions state
  const [savedSuggestions, setSavedSuggestions] = useState<SavedSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [savingSuggestion, setSavingSuggestion] = useState<number | null>(null);
  const [completionModal, setCompletionModal] = useState<{ id: number; title: string } | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const {
    panelRef: completionPanelRef,
    panelStyle: completionPanelStyle,
    dragHandleProps: completionDragHandleProps,
  } = useModalDrag(completionModal !== null);

  // Expanded row state
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Stats
  const stats = useMemo(() => {
    const total = totalCount;
    const successful = logs.filter(l => l.success).length;
    const failed = logs.filter(l => !l.success).length;
    const avgResponseTime = logs.length > 0
      ? Math.round(logs.reduce((sum, l) => sum + (l.response_time_ms || 0), 0) / logs.length)
      : 0;
    return { total, successful, failed, avgResponseTime };
  }, [logs, totalCount]);

  const fetchLogs = async (resetPage = false) => {
    setLoading(true);
    setError(null);
    const currentPage = resetPage ? 0 : page;

    try {
      const response = await aiService.getChatLogs({
        limit: pageSize,
        offset: currentPage * pageSize,
        success_filter: successFilter || undefined,
        user_email_filter: userFilter || undefined,
      });

      setLogs(response.logs);
      setTotalCount(response.total_count);
      setHasMore(response.has_more);

      if (resetPage) {
        setPage(0);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch chat logs');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await aiService.analyzeFailures();
      setAnalysisResult(result);
      setShowAnalysis(true);
    } catch (err: any) {
      setAnalysisError(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchIndexStatus = async () => {
    try {
      const status = await aiService.getHelpChatStatus();
      setIndexStatus(status);
    } catch (err: any) {
      console.error('Failed to fetch index status:', err);
    }
  };

  const fetchIndexedDocuments = async () => {
    try {
      const docs = await aiService.getIndexedDocuments();
      setIndexedPages(docs.pages || []);
    } catch (err: any) {
      console.error('Failed to fetch indexed documents:', err);
    }
  };

  const handleDeletePage = async (pageName: string) => {
    if (!confirm(`Are you sure you want to delete "${pageName}"? This will remove all vectors for this document.`)) {
      return;
    }

    setDeletingPage(pageName);
    try {
      const result = await aiService.deletePage(pageName);
      setUploadSuccess(`Deleted "${pageName}" (${result.vectors_deleted} vectors removed)`);
      fetchIndexStatus();
      fetchIndexedDocuments();
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || `Failed to delete ${pageName}`);
    } finally {
      setDeletingPage(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
      setUploadSuccess(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !pageName.trim()) {
      setUploadError('Please select a file and enter a page name');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const result = await aiService.uploadDocument(selectedFile, pageName.trim());
      setUploadSuccess(`Successfully indexed "${result.filename}" with ${result.chunks_indexed} chunks`);
      setSelectedFile(null);
      setPageName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchIndexStatus();
      fetchIndexedDocuments();
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleClearIndex = async () => {
    if (!confirm('Are you sure you want to clear all indexed documents? This cannot be undone.')) {
      return;
    }

    setClearing(true);
    try {
      await aiService.clearIndex();
      setUploadSuccess('Index cleared successfully');
      fetchIndexStatus();
      fetchIndexedDocuments();
    } catch (err: any) {
      setUploadError(err.response?.data?.detail || 'Failed to clear index');
    } finally {
      setClearing(false);
    }
  };

  const fetchSavedSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await aiService.getSuggestions();
      setSavedSuggestions(response.suggestions);
    } catch (err: any) {
      console.error('Failed to fetch saved suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSaveSuggestion = async (suggestion: Suggestion, idx: number) => {
    setSavingSuggestion(idx);
    try {
      await aiService.saveSuggestion({
        type: suggestion.type,
        priority: suggestion.priority,
        title: suggestion.title,
        description: suggestion.description,
        implementation_notes: suggestion.implementation_notes,
        questions_addressed: suggestion.questions_addressed,
      });
      fetchSavedSuggestions();
    } catch (err: any) {
      console.error('Failed to save suggestion:', err);
    } finally {
      setSavingSuggestion(null);
    }
  };

  const handleMarkComplete = async () => {
    if (!completionModal) return;

    setUpdatingStatus(true);
    try {
      await aiService.updateSuggestion(completionModal.id, {
        status: 'completed',
        completion_notes: completionNotes,
      });
      setCompletionModal(null);
      setCompletionNotes('');
      fetchSavedSuggestions();
    } catch (err: any) {
      console.error('Failed to mark suggestion complete:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      await aiService.updateSuggestion(id, { status });
      fetchSavedSuggestions();
    } catch (err: any) {
      console.error('Failed to update suggestion status:', err);
    }
  };

  const handleDeleteSuggestion = async (id: number) => {
    if (!confirm('Are you sure you want to delete this suggestion?')) return;
    try {
      await aiService.deleteSuggestion(id);
      fetchSavedSuggestions();
    } catch (err: any) {
      console.error('Failed to delete suggestion:', err);
    }
  };

  useEffect(() => {
    fetchLogs(true);
    fetchIndexStatus();
    fetchIndexedDocuments();
    fetchSavedSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successFilter, userFilter]);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-danger text-danger-fg border-red-200';
      case 'medium': return 'bg-flag text-flag-fg border-yellow-200';
      case 'low': return 'bg-ok text-ok-fg border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-rule dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'endpoint': return <CodeBracketIcon className="w-4 h-4" />;
      case 'documentation': return <DocumentTextIcon className="w-4 h-4" />;
      case 'tool': return <WrenchScrewdriverIcon className="w-4 h-4" />;
      case 'enhancement': return <LightBulbIcon className="w-4 h-4" />;
      default: return <LightBulbIcon className="w-4 h-4" />;
    }
  };

  const columns: ColumnDef<ChatLog>[] = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 60,
    },
    {
      accessorKey: 'created_at',
      header: 'Time',
      size: 150,
      cell: ({ row }) => formatTimestamp(row.original.created_at),
    },
    {
      accessorKey: 'user_email',
      header: 'User',
      size: 180,
      cell: ({ row }) => (
        <span className="text-sm truncate" title={row.original.user_email}>
          {row.original.user_email.split('@')[0]}
        </span>
      ),
    },
    {
      accessorKey: 'question',
      header: 'Question',
      size: 300,
      cell: ({ row }) => (
        <div className="max-w-[300px] truncate" title={row.original.question}>
          {row.original.question}
        </div>
      ),
    },
    {
      accessorKey: 'success',
      header: 'Status',
      size: 100,
      cell: ({ row }) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
          row.original.success
            ? 'bg-ok text-ok-fg'
            : 'bg-danger text-danger-fg'
        }`}>
          {row.original.success ? (
            <><CheckCircleIcon className="w-3 h-3" /> Success</>
          ) : (
            <><XCircleIcon className="w-3 h-3" /> Failed</>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'tools_used',
      header: 'Tools',
      size: 150,
      cell: ({ row }) => {
        const tools = row.original.tools_used || [];
        if (tools.length === 0) return <span className="text-gray-400">None</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {tools.map((t, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {t.tool.replace('_', ' ')}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'response_time_ms',
      header: 'Time (ms)',
      size: 90,
      cell: ({ row }) => (
        <span className={`${
          (row.original.response_time_ms || 0) > 5000 ? 'text-red-600' : 'text-gray-600'
        }`}>
          {row.original.response_time_ms?.toLocaleString() || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'page_context',
      header: 'Page',
      size: 120,
      cell: ({ row }) => (
        <span className="text-xs text-gray-500 truncate" title={row.original.page_context || ''}>
          {row.original.page_context || '-'}
        </span>
      ),
    },
    {
      id: 'expand',
      header: '',
      size: 40,
      cell: ({ row }) => (
        <button
          onClick={() => setExpandedRow(expandedRow === row.original.id ? null : row.original.id)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          {expandedRow === row.original.id ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      ),
    },
  ], [expandedRow]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-2xl font-semibold text-heading">
              AI Management
            </h1>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Monitor help chat usage and analyze unanswered questions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchLogs(true)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <SparklesIcon className={`w-4 h-4 ${analyzing ? 'animate-pulse' : ''}`} />
            {analyzing ? 'Analyzing...' : 'Analyze Failed Questions'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Questions</div>
          <div className="text-2xl font-bold text-heading">{totalCount.toLocaleString()}</div>
        </div>
        <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Success Rate</div>
          <div className="text-2xl font-bold text-green-600">
            {logs.length > 0 ? Math.round((stats.successful / logs.length) * 100) : 0}%
          </div>
        </div>
        <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Failed (this page)</div>
          <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
        </div>
        <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Avg Response Time</div>
          <div className="text-2xl font-bold text-heading">{stats.avgResponseTime}ms</div>
        </div>
      </div>

      {/* Document Management Section */}
      <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentArrowUpIcon className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-heading">
                Knowledge Base Documents
              </h2>
            </div>
            {indexStatus && (
              <div className="flex items-center gap-4 text-sm">
                <span className={`flex items-center gap-1 ${indexStatus.status === 'ready' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {indexStatus.status === 'ready' ? (
                    <CheckCircleIcon className="w-4 h-4" />
                  ) : (
                    <ExclamationTriangleIcon className="w-4 h-4" />
                  )}
                  {indexStatus.status === 'ready' ? 'Connected' : 'Not Configured'}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {indexStatus.document_count.toLocaleString()} vectors indexed
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Status indicators */}
          {indexStatus && (
            <div className="flex flex-wrap gap-3 text-sm">
              <span className={`px-2 py-1 rounded ${indexStatus.pinecone_configured ? 'bg-ok text-ok-fg' : 'bg-danger text-danger-fg'}`}>
                Pinecone: {indexStatus.pinecone_configured ? 'Connected' : 'Not Configured'}
              </span>
              <span className={`px-2 py-1 rounded ${indexStatus.openai_configured ? 'bg-ok text-ok-fg' : 'bg-danger text-danger-fg'}`}>
                OpenAI: {indexStatus.openai_configured ? 'Connected' : 'Not Configured'}
              </span>
              <span className={`px-2 py-1 rounded ${indexStatus.anthropic_configured ? 'bg-ok text-ok-fg' : 'bg-danger text-danger-fg'}`}>
                Anthropic: {indexStatus.anthropic_configured ? 'Connected' : 'Not Configured'}
              </span>
              <span className={`px-2 py-1 rounded ${indexStatus.index_exists ? 'bg-ok text-ok-fg' : 'bg-flag text-flag-fg'}`}>
                Index: {indexStatus.index_exists ? 'Exists' : 'Not Created'}
              </span>
            </div>
          )}

          {/* Upload form */}
          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-label mb-1">
                  Document File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.markdown,.html,.htm,.pdf"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded file:border-0
                    file:text-sm file:font-medium
                    file:bg-indigo-50 file:text-indigo-700
                    hover:file:bg-indigo-100
                    dark:file:bg-indigo-900/50 dark:file:text-indigo-300"
                />
                <p className="mt-1 text-xs text-gray-500">Accepts .txt, .md, .html, .pdf files</p>
              </div>
              <div className="w-full md:w-64">
                <label className="block text-sm font-medium text-label mb-1">
                  Page/Category Name
                </label>
                <input
                  type="text"
                  value={pageName}
                  onChange={(e) => setPageName(e.target.value)}
                  placeholder="e.g., Dispatch Planner Help"
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-heading focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile || !pageName.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudArrowUpIcon className={`w-4 h-4 ${uploading ? 'animate-pulse' : ''}`} />
                {uploading ? 'Uploading...' : 'Upload & Index'}
              </button>
            </div>

            {selectedFile && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Success/Error messages */}
          {uploadSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg">
              <CheckCircleIcon className="w-5 h-5" />
              {uploadSuccess}
            </div>
          )}
          {uploadError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-lg">
              <ExclamationTriangleIcon className="w-5 h-5" />
              {uploadError}
            </div>
          )}

          {/* Indexed Documents List */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-label mb-3">
              Indexed Documents ({indexedPages.length})
            </h3>
            {indexedPages.length === 0 ? (
              <div className="text-sm text-muted text-center py-4">
                No documents indexed yet. Upload a document above to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {indexedPages.map((page) => (
                  <div
                    key={page.page}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-heading truncate">
                        {page.page}
                      </div>
                      <div className="text-xs text-muted">
                        {page.vector_count} vectors
                        {page.sample_sections.length > 0 && (
                          <span> &bull; Sections: {page.sample_sections.slice(0, 3).join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeletePage(page.page)}
                      disabled={deletingPage === page.page}
                      className="ml-3 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                      title={`Delete ${page.page}`}
                    >
                      {deletingPage === page.page ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Clear index button */}
          {(indexStatus?.document_count ?? 0) > 0 && (
            <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleClearIndex}
                disabled={clearing}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              >
                <TrashIcon className={`w-4 h-4 ${clearing ? 'animate-pulse' : ''}`} />
                {clearing ? 'Clearing...' : 'Clear All Documents'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Results */}
      {showAnalysis && analysisResult && (
        <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-semibold text-heading">
                Analysis Results
              </h2>
              <span className="text-sm text-gray-500">
                ({analysisResult.failures_analyzed} failed questions analyzed)
              </span>
            </div>
            <button
              onClick={() => setShowAnalysis(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-6">
            {/* Summary */}
            {analysisResult.summary && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
                <h3 className="font-medium text-indigo-900 dark:text-indigo-100 mb-2">Summary</h3>
                <p className="text-sm text-indigo-800 dark:text-indigo-200">{analysisResult.summary}</p>
              </div>
            )}

            {/* Top Question Themes */}
            {analysisResult.top_question_themes && analysisResult.top_question_themes.length > 0 && (
              <div>
                <h3 className="font-medium text-heading mb-3">Top Question Themes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {analysisResult.top_question_themes.map((theme: QuestionTheme, idx: number) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-heading">{theme.theme}</span>
                        <span className="px-2 py-0.5 bg-info text-info-fg rounded text-xs font-medium">
                          {theme.count} questions
                        </span>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {theme.example_questions?.slice(0, 2).map((q: string, i: number) => (
                          <li key={i} className="truncate" title={q}>• {q}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions */}
            {analysisResult.suggestions && analysisResult.suggestions.length > 0 && (
              <div>
                <h3 className="font-medium text-heading mb-3">Improvement Suggestions</h3>
                <div className="space-y-3">
                  {analysisResult.suggestions.map((suggestion: Suggestion, idx: number) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border-l-4 border-indigo-500">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getTypeIcon(suggestion.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-heading">
                                {suggestion.title}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                                {suggestion.priority}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-label rounded text-xs">
                                {suggestion.type}
                              </span>
                            </div>
                            {savedSuggestions.some(s => s.title === suggestion.title) ? (
                              <span className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                <CheckCircleIcon className="w-3 h-3" />
                                Tracked
                              </span>
                            ) : (
                              <button
                                onClick={() => handleSaveSuggestion(suggestion, idx)}
                                disabled={savingSuggestion === idx}
                                className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {savingSuggestion === idx ? 'Saving...' : 'Save to Track'}
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {suggestion.description}
                          </p>
                          {suggestion.implementation_notes && (
                            <div className="bg-panel p-2 rounded text-xs text-label mb-2">
                              {suggestion.implementation_notes}
                            </div>
                          )}
                          {suggestion.questions_addressed && suggestion.questions_addressed.length > 0 && (
                            <div className="text-xs text-muted">
                              <span className="font-medium">Would help answer:</span>
                              <ul className="mt-1 space-y-0.5">
                                {suggestion.questions_addressed.slice(0, 3).map((q: string, i: number) => (
                                  <li key={i} className="truncate" title={q}>• {q}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {analysisResult.message && (
              <div className="text-center text-gray-500 py-4">
                {analysisResult.message}
              </div>
            )}
          </div>
        </div>
      )}

      {analysisError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {analysisError}
        </div>
      )}

      {/* Saved Suggestions Section */}
      <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LightBulbIcon className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-semibold text-heading">
              Tracked Improvement Suggestions
            </h2>
            <span className="text-sm text-gray-500">
              ({savedSuggestions.length} total)
            </span>
          </div>
          <button
            onClick={fetchSavedSuggestions}
            disabled={loadingSuggestions}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loadingSuggestions ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="p-4">
          {savedSuggestions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No suggestions tracked yet. Run an analysis and save suggestions to track them here.
            </div>
          ) : (
            <div className="space-y-3">
              {savedSuggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    suggestion.status === 'completed'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                      : suggestion.status === 'in_progress'
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                      : suggestion.status === 'dismissed'
                      ? 'bg-gray-50 dark:bg-gray-700 border-gray-400 opacity-60'
                      : 'bg-gray-50 dark:bg-gray-700 border-indigo-500'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 mt-0.5">
                        {getTypeIcon(suggestion.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-heading">
                            {suggestion.title}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(suggestion.priority)}`}>
                            {suggestion.priority}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-label rounded text-xs">
                            {suggestion.type}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            suggestion.status === 'completed'
                              ? 'bg-ok text-ok-fg'
                              : suggestion.status === 'in_progress'
                              ? 'bg-info text-info-fg'
                              : suggestion.status === 'dismissed'
                              ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              : 'bg-flag text-flag-fg'
                          }`}>
                            {suggestion.status.replace('_', ' ')}
                          </span>
                        </div>
                        {suggestion.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {suggestion.description}
                          </p>
                        )}
                        {suggestion.completion_notes && (
                          <div className="mt-2 p-2 bg-green-100 dark:bg-green-900/30 rounded text-sm text-green-800 dark:text-green-200">
                            <span className="font-medium">Completion Notes:</span> {suggestion.completion_notes}
                          </div>
                        )}
                        {suggestion.completed_at && (
                          <div className="mt-1 text-xs text-gray-500">
                            Completed: {new Date(suggestion.completed_at).toLocaleString()}
                            {suggestion.completed_by && ` by ${suggestion.completed_by}`}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {suggestion.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUpdateStatus(suggestion.id, 'in_progress')}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Start
                          </button>
                          <button
                            onClick={() => setCompletionModal({ id: suggestion.id, title: suggestion.title })}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(suggestion.id, 'dismissed')}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
                          >
                            Dismiss
                          </button>
                        </>
                      )}
                      {suggestion.status === 'in_progress' && (
                        <button
                          onClick={() => setCompletionModal({ id: suggestion.id, title: suggestion.title })}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Mark Complete
                        </button>
                      )}
                      {suggestion.status !== 'completed' && (
                        <button
                          onClick={() => handleDeleteSuggestion(suggestion.id)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Completion Modal */}
      {completionModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={completionPanelRef}
            style={completionPanelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <ModalHeader
              title="Mark as Complete"
              onClose={
                updatingStatus
                  ? undefined
                  : () => {
                      setCompletionModal(null);
                      setCompletionNotes('');
                    }
              }
              dragHandleProps={completionDragHandleProps}
            />
            <div className="p-4">
              <p className="text-sm text-gray-500 mb-3">{completionModal.title}</p>
              <label className="block text-sm font-medium text-label mb-2">
                Completion Notes (what was implemented/fixed)
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Describe what was added or fixed..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-heading focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setCompletionModal(null);
                  setCompletionNotes('');
                }}
                className="px-4 py-2 text-sm text-label hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkComplete}
                disabled={updatingStatus}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {updatingStatus ? 'Saving...' : 'Mark Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-label mb-1">
              Status
            </label>
            <select
              value={successFilter}
              onChange={(e) => setSuccessFilter(e.target.value as any)}
              className="block w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-heading focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="">All</option>
              <option value="true">Success</option>
              <option value="false">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-label mb-1">
              User Email
            </label>
            <input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Filter by email..."
              className="block w-60 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-heading focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-800">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <TanTable
          data={logs}
          columns={columns}
          loading={loading}
        />

        {/* Expanded Row Details */}
        {expandedRow !== null && logs.find(log => log.id === expandedRow) && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            {(() => {
              const log = logs.find(l => l.id === expandedRow);
              if (!log) return null;
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-heading mb-2">Question</h4>
                    <p className="text-sm text-label whitespace-pre-wrap">
                      {log.question}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-heading mb-2">Answer</h4>
                    <div className="text-sm text-label max-h-48 overflow-y-auto whitespace-pre-wrap">
                      {log.answer || <span className="text-gray-400">No answer</span>}
                    </div>
                  </div>
                  {log.error_message && (
                    <div className="md:col-span-2">
                      <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Error</h4>
                      <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                        {log.error_message}
                      </p>
                    </div>
                  )}
                  {log.tools_used && log.tools_used.length > 0 && (
                    <div className="md:col-span-2">
                      <h4 className="font-medium text-heading mb-2">Tools Used</h4>
                      <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.tools_used, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0 || loading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 hover:bg-highlight"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page + 1} of {Math.ceil(totalCount / pageSize) || 1}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!hasMore || loading}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 hover:bg-highlight"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIManagement;
